/**
 * PropertyUpdatedItem - Renders property update events with cascades.
 *
 * Receives item.events[] where events[0] is the primary update and
 * events[1..N] are cascade events (formula recomputations, etc.).
 * Combines all property changes into a single timeline entry.
 *
 * Display: "**Actor** updated **N fields** on this contact" with sub-items
 * showing PropertyName: oldValue → newValue for all events combined.
 */

import { Edit03 as EditIcon } from '@untitledui/icons';
import type { ReactElement, ReactNode } from 'react';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { EntityBadge } from '@/lib/crm/components/badges/EntityBadge';
import { useCrmObjectSnapshot } from '@/lib/crm/hooks/useCrmObjectSnapshot';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { isAnswersSnapshotProperty } from '@/lib/crm/utils/bookingQuestions';
import { getEntityTypeSentenceLabel } from '@/lib/crm/utils/entityTypeUtils';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';
import { unwrapPropertyValue } from '@/types/crm';

import {
  ActorName,
  bold,
  InlineText,
  PropertyDiffValue,
  TimelineEvent,
} from '../components/shared';
import { useViewStateLookups } from '../context/TimelineLookupsContext';
import { collectVisibleUpdatedProperties } from '../timelineItemVisibility';
import type { SubItemContent } from '../types/renderer';
import { useEventFormatters } from '../useEventFormatters';
import { buildBookingAnswerSubItems } from '../utils/buildBookingAnswerSubItems';
import {
  buildJsonSubItems,
  getCompanionSchema,
} from '../utils/buildJsonSubItems';
import { getTimelineValueText } from '../utils/valueText';
import type { ItemComponentProps } from './types';

/**
 * Multi-field updates with more than this many changes collapse the extras
 * behind a "+N more changes" toggle so bulk edits / imports don't flood the
 * timeline. Updates of 3 or fewer fields render in full.
 */
const PROPERTY_UPDATE_COLLAPSE_THRESHOLD = 3;

export function PropertyUpdatedItem({ item, entityType }: ItemComponentProps) {
  const { orgId: orgIdParam } = useParams<{ orgId: string }>();
  const orgId = orgIdParam ? Number(orgIdParam) : undefined;
  const { getActorInfo, formatPropertyName, getObjectLabel } =
    useEventFormatters();
  const { lookups } = useViewStateLookups();
  const openSlideout = useOpenSlideoutOptional();

  const handleEntityClick = useCallback(
    (entityId: string) => {
      openSlideout?.(entityId);
    },
    [openSlideout]
  );

  const event = item.events[0];
  const actor = getActorInfo(event);

  // Get property schema from lookups
  const getPropertySchema = (
    propertyName: string
  ): PropertyTypeSchema | undefined => {
    const key = `${event.entity_type_name}.${propertyName}`;
    return lookups.property_types[key];
  };

  // Get icon for a property using schema lookup
  const getPropertyIcon = (propertyName: string): ReactElement => {
    const iconClass = 'w-4 h-4 text-primary';
    const propertySchema = getPropertySchema(propertyName);

    if (propertySchema) {
      const IconComponent = getPropertyTypeIconComponent(propertySchema);
      return <IconComponent className={iconClass} />;
    }

    return <EditIcon className={iconClass} />;
  };

  // Check if this is an associated event (from a different entity)
  const isAssociatedEvent = 'association_type_name' in event;

  // For associated events, get the full object from lookups for badge display
  const associatedObject = isAssociatedEvent
    ? lookups.entities[event.entity_id]
    : undefined;

  // The entity's current properties (from the lookups pool), forwarded to
  // PropertyDiffValue so a `formula` field can resolve its target entity type
  // from a sibling property.
  // Read the full entity from the store (kept live via WebSocket) so a formula
  // label can resolve its target type from ahoy_target_entity_type. The light
  // timeline `lookups.entities` projection only carries the display name, so an
  // update event (which changed only one field) can't resolve it otherwise.
  const storeObject = useCrmObjectSnapshot(event.entity_id, orgId);
  const entityProperties =
    storeObject?.properties ?? lookups.entities[event.entity_id]?.properties;

  const objectDisplayText = isAssociatedEvent
    ? getObjectLabel(event.entity_id)
    : `this ${getEntityTypeSentenceLabel(entityType)}`;

  const handleAssociatedObjectClick = useCallback(() => {
    if (associatedObject) {
      handleEntityClick(associatedObject.entity_id);
    }
  }, [handleEntityClick, associatedObject]);

  const isNoteObject = associatedObject?.entity_type_name === 'ahoy_note';
  let objectDisplay: ReactNode;
  if (isAssociatedEvent && associatedObject && !isNoteObject) {
    objectDisplay = (
      <EntityBadge
        state={{
          kind: 'data',
          data: {
            id: associatedObject.entity_id,
            entityType: associatedObject.entity_type_name,
            displayName: associatedObject.ahoy_display_name ?? 'Unknown',
          },
        }}
        className='inline-flex align-middle'
        onClick={openSlideout ? handleAssociatedObjectClick : undefined}
      />
    );
  } else if (isAssociatedEvent) {
    objectDisplay = bold(objectDisplayText);
  } else {
    objectDisplay = objectDisplayText;
  }

  // Old values from ALL events (primary + cascades), first-wins (the first
  // event's old values are the true pre-change state).
  const allOldPropertyRecords: Record<
    string,
    { value: unknown; [key: string]: unknown }
  > = {};

  for (const ev of item.events) {
    const oldRecords = ev.internal_payload?.old_property_values || {};
    for (const [key, val] of Object.entries(oldRecords)) {
      if (!(key in allOldPropertyRecords)) {
        allOldPropertyRecords[key] = val as {
          value: unknown;
          [key: string]: unknown;
        };
      }
    }
  }

  // Merge new values across events (last-wins) and keep only readable
  // properties. When nothing is readable the entry renders nothing — drop it.
  const visibleProperties = collectVisibleUpdatedProperties(
    item.events,
    lookups
  );

  if (visibleProperties.length === 0) {
    return null;
  }

  // Build sub-items list for all property updates.
  // JSON properties with a companion schema are decomposed into per-field sub-items.
  const subItems: SubItemContent[] = visibleProperties.flatMap(
    ([propertyName, newValue]) => {
      const schema = getPropertySchema(propertyName);

      // JSON decomposition
      const companionSchema = getCompanionSchema(
        schema,
        event.internal_payload
      );
      if (
        companionSchema &&
        typeof newValue === 'object' &&
        newValue !== null &&
        !Array.isArray(newValue)
      ) {
        return buildJsonSubItems(
          newValue as Record<string, unknown>,
          companionSchema,
          lookups,
          openSlideout ? handleEntityClick : undefined
        );
      }

      const oldRecord = allOldPropertyRecords[propertyName];
      const oldValue = oldRecord?.value;

      // Answers-snapshot decomposition: one sub-item per changed booking
      // question, each an old → new diff (see buildBookingAnswerSubItems).
      if (isAnswersSnapshotProperty(propertyName)) {
        return buildBookingAnswerSubItems({
          value: newValue,
          previousValue: oldValue,
          orgId,
        });
      }

      const displayName = formatPropertyName(
        propertyName,
        event.entity_type_name
      );
      const isUrl = isUrlProperty(propertyName, newValue);
      const icon = getPropertyIcon(propertyName);

      // URL properties render as plain links via the `href` sub-item field
      // (see `!isUrl && …` in the JSX below), so we only use the diff view
      // for non-URL properties.
      const valueDisplay: ReactNode = (
        <PropertyDiffValue
          oldValue={oldValue}
          newValue={newValue}
          schema={schema}
          orgId={orgId}
          entityProperties={entityProperties}
        />
      );

      const fullUrl = isUrl ? getFullUrl(newValue) : undefined;
      const valueText = getTimelineValueText(newValue);

      return {
        icon,
        text: (
          <span>
            {displayName}: {!isUrl && valueDisplay}
          </span>
        ),
        ...(isUrl &&
          fullUrl && {
            href: fullUrl,
            linkText: valueText,
          }),
        ariaLabel: `${displayName}: ${valueText}`,
      };
    }
  );

  const propertyCount = visibleProperties.length;
  const fieldLabel =
    propertyCount === 1 ? '1 field' : `${propertyCount} fields`;
  const ariaLabel = `${actor.name} updated ${fieldLabel} on ${objectDisplayText}`;

  return (
    <TimelineEvent ariaLabel={ariaLabel}>
      <TimelineEvent.Icon event={event} />
      <TimelineEvent.Content
        header={
          <InlineText>
            <ActorName actor={actor} /> updated {bold(fieldLabel)} on{' '}
            {objectDisplay}
          </InlineText>
        }
        timestamp={event.occurred_at}
        subItems={subItems}
        subItemsCollapseAfter={PROPERTY_UPDATE_COLLAPSE_THRESHOLD}
      />
    </TimelineEvent>
  );
}

// Helper functions

function isUrlProperty(propertyName: string, value: unknown): boolean {
  const name = propertyName.toLowerCase();
  const urlFields = ['linkedin', 'url', 'website', 'domain'];

  if (!urlFields.some(field => name.includes(field))) {
    return false;
  }

  const strValue = getTimelineValueText(value);
  return strValue.startsWith('http://') || strValue.startsWith('https://');
}

function getFullUrl(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value.startsWith('http') ? value : undefined;
  }
  // timeline event payload: single-level {value} wrapper — unwrap via unwrapPropertyValue
  const inner = unwrapPropertyValue(value);
  return typeof inner === 'string' && inner.startsWith('http')
    ? inner
    : undefined;
}
